import { supabase } from '@/lib/supabase';
import { products, type Product } from '@/mocks/products';
import { categories, type Category } from '@/mocks/categories';
import { reviews, type Review } from '@/mocks/reviews';
import { blogPosts, type BlogPost } from '@/mocks/blogPosts';
import { markCatalogUpdated } from '@/lib/catalogStore';

const TONES: Category['tone'][] = ['primary', 'accent', 'secondary'];

// 방문할 때마다 Supabase에 실시간으로 4개 쿼리를 물어보고 그 응답을 기다린 뒤에야
// 화면 전체(버튼 포함)가 나타나던 게 체감 로딩 지연의 핵심 원인이었습니다.
// 이제는 마지막으로 불러온 데이터를 브라우저(localStorage)에 저장해뒀다가,
// 재방문 시에는 그 캐시로 즉시 화면을 그리고, 최신 데이터는 뒤에서 조용히 갱신합니다.
// (= "매번 서버에 물어보고 기다리는 구조" -> "캐시로 먼저 보여주고 나중에 갱신하는 구조")
const CACHE_KEY = 'handled_catalog_cache_v1';

interface CacheShape {
  savedAt: number;
  products: Product[];
  categories: Category[];
  reviews: Review[];
  blogPosts: BlogPost[];
}

/**
 * localStorage에 저장된 캐시가 있으면 즉시(동기적으로) 배열들을 채우고 true를 반환합니다.
 * 네트워크 요청이 전혀 없으므로 화면을 기다리지 않고 바로 그릴 수 있습니다.
 */
export function hydrateFromCache(): boolean {
  try {
    const raw = localStorage.getItem(CACHE_KEY);
    if (!raw) return false;
    const cache = JSON.parse(raw) as CacheShape;
    if (!cache || !Array.isArray(cache.products)) return false;

    products.length = 0;
    products.push(...cache.products);
    categories.length = 0;
    categories.push(...cache.categories);
    reviews.length = 0;
    reviews.push(...cache.reviews);
    blogPosts.length = 0;
    blogPosts.push(...cache.blogPosts);
    markCatalogUpdated();
    return true;
  } catch {
    return false;
  }
}

function persistToCache() {
  try {
    const payload: CacheShape = {
      savedAt: Date.now(),
      products,
      categories,
      reviews,
      blogPosts,
    };
    localStorage.setItem(CACHE_KEY, JSON.stringify(payload));
  } catch {
    // localStorage가 꽉 찼거나 접근 불가한 환경이면 캐싱만 건너뜁니다.
  }
}

export async function bootstrapCatalog(): Promise<void> {
  try {
    // Run all independent reads in parallel instead of one-after-another —
    // on mobile networks, 4 sequential round trips can add a second or more
    // of pure waiting time before the page renders anything.
    const [catRes, productRes, reviewRes, blogRes] = await Promise.all([
      supabase.from('categories').select('slug, name, en_name, icon, tone'),
      supabase
        .from('products')
        .select(
          'id, title, description, category_slug, price, rating, review_count, sales_count, seller_name, image_url, badge, usage_description, usage_image_url, usage_video_url',
        )
        .eq('status', 'on_sale')
        .order('created_at', { ascending: false }),
      supabase
        .from('reviews')
        .select('id, product_id, author_name, rating, content, created_at')
        .order('created_at', { ascending: false }),
      supabase
        .from('blog_posts')
        .select('id, title, excerpt, content, cover_image_url, author_name, created_at')
        .order('created_at', { ascending: false }),
    ]);

    const catRows = catRes.data;
    const productRows = productRes.data;
    const reviewRows = reviewRes.data;
    const blogRows = blogRes.data;

    const nameMap: Record<string, string> = {};
    (catRows ?? []).forEach((c) => {
      nameMap[c.slug] = c.name;
    });

    const mappedProducts: Product[] = (productRows ?? []).map((p) => ({
      id: p.id,
      title: p.title,
      description: p.description ?? '',
      category: nameMap[p.category_slug] ?? p.category_slug,
      categorySlug: p.category_slug,
      price: Number(p.price),
      rating: Number(p.rating ?? 0),
      reviews: p.review_count ?? 0,
      sales: p.sales_count ?? 0,
      seller: p.seller_name ?? '',
      image: p.image_url ?? '',
      badge: (p.badge as Product['badge']) ?? undefined,
      usageDescription: p.usage_description ?? undefined,
      usageImageUrl: p.usage_image_url ?? undefined,
      usageVideoUrl: p.usage_video_url ?? undefined,
    }));

    products.length = 0;
    products.push(...mappedProducts);

    const counts: Record<string, number> = {};
    mappedProducts.forEach((p) => {
      counts[p.categorySlug] = (counts[p.categorySlug] ?? 0) + 1;
    });

    const mappedCategories: Category[] = (catRows ?? []).map((c, i) => ({
      slug: c.slug,
      name: c.name,
      enName: c.en_name,
      icon: c.icon,
      tone: (TONES.includes(c.tone as Category['tone']) ? c.tone : TONES[i % 3]) as Category['tone'],
      count: counts[c.slug] ?? 0,
    }));

    categories.length = 0;
    categories.push(...mappedCategories);

    const mappedReviews: Review[] = (reviewRows ?? []).map((r) => ({
      id: r.id,
      productId: r.product_id,
      author: r.author_name ?? '익명',
      rating: r.rating,
      content: r.content ?? '',
      date: (r.created_at as string).slice(0, 10),
    }));

    reviews.length = 0;
    reviews.push(...mappedReviews);

    const mappedBlogPosts: BlogPost[] = (blogRows ?? []).map((b) => ({
      id: b.id,
      title: b.title,
      excerpt: b.excerpt ?? '',
      content: b.content ?? '',
      coverImageUrl: b.cover_image_url ?? '',
      authorName: b.author_name ?? '관리자',
      createdAt: (b.created_at as string).slice(0, 10),
    }));

    blogPosts.length = 0;
    blogPosts.push(...mappedBlogPosts);

    persistToCache();
    markCatalogUpdated();
  } catch (e) {
    console.error('bootstrapCatalog failed', e);
  }
}
